const cardSelector = '.save-time__card-list-item';
const textSelector = '.save-time__card-copy';
const geometryOptions = {
    duration: 1000,
    easing: 'linear',
    fill: 'both',
};

// Пружина с mass 1, stiffness 100 и damping 14, как у раскрывающихся карточек референса.
function geometryFrames(from, to) {
    return Array.from({ length: 61 }, (_, index) => {
        const offset = index / 60;
        const progress = index === 60 ? 1 : 1 - Math.exp(-7 * offset) * (
            Math.cos(Math.sqrt(51) * offset) + 7 / Math.sqrt(51) * Math.sin(Math.sqrt(51) * offset)
        );
        return {
            offset,
            width: `${from.width + (to.width - from.width) * progress}px`,
            height: `${from.height + (to.height - from.height) * progress}px`,
        };
    });
}

export function initAdvantagesSaveTimeSection() {
    document.querySelectorAll('.save-time').forEach(section => {
        if (section.dataset.saveTimeInitialized) {
            return;
        }
        section.dataset.saveTimeInitialized = 'true';

        const items = Array.from(section.querySelectorAll(cardSelector));
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        let selectedItem = items.find(item => item.dataset.activeDefault === 'true')
            || items.find(item => item.classList.contains('active'))
            || items[Math.min(2, items.length - 1)]
            || null;
        let transitionId = 0;
        let cardAnimations = [];

        const cancelCardAnimations = () => {
            cardAnimations.forEach(animation => animation.cancel());
            cardAnimations = [];
        };

        const clearSizes = () => {
            items.forEach(item => {
                item.style.removeProperty('width');
                item.style.removeProperty('height');
                item.querySelector(textSelector).style.removeProperty('width');
            });
        };

        const applySelection = () => {
            items.forEach(item => {
                const active = item === selectedItem;
                item.classList.toggle('active', active);
                item.classList.toggle('save-time__card-list-item--selected', active);
                item.setAttribute('aria-expanded', String(active));
            });
        };

        const selectItem = async (item) => {
            if (item === selectedItem) {
                return;
            }
            const id = ++transitionId;
            selectedItem = item;
            items.forEach(card => {
                card.classList.toggle('save-time__card-list-item--selected', card === selectedItem);
            });
            section.classList.add('save-time--switching');

            // Снимок текущей геометрии позволяет продолжить переход при повторном клике.
            const sizes = items.map(card => {
                const rect = card.getBoundingClientRect();
                const text = card.querySelector(textSelector);
                return {
                    card, text, width: rect.width, height: rect.height,
                    copyWidth: text.getBoundingClientRect().width,
                    opacity: Number(getComputedStyle(text).opacity),
                };
            });
            cancelCardAnimations();
            clearSizes();

            if (reducedMotion.matches) {
                applySelection();
                section.classList.remove('save-time--switching');
                return;
            }

            const changedText = sizes.filter(({ card, opacity }) =>
                card.classList.contains('active') !== (card === selectedItem) || opacity < 1
            );

            // Удерживаем текущую геометрию только у меняющихся карточек на время ухода текста.
            sizes.forEach(({ card, text, width, height, copyWidth, opacity }) => {
                const rect = card.getBoundingClientRect();
                if (card.classList.contains('active') !== (card === selectedItem)
                    || opacity < 1
                    || Math.abs(width - rect.width) >= 0.5
                    || Math.abs(height - rect.height) >= 0.5) {
                    card.style.width = `${width}px`;
                    card.style.height = `${height}px`;
                    text.style.width = `${copyWidth}px`;
                }
            });

            try {
                cardAnimations = changedText.map(({ text, opacity }) => text.animate(
                    [{ opacity }, { opacity: 0 }],
                    { duration: 120, easing: 'ease-out', fill: 'both' }
                ));
                await Promise.all(cardAnimations.map(animation => animation.finished));
                if (id !== transitionId) {
                    return;
                }

                applySelection();
                clearSizes();
                const targets = sizes.map(({ card, text }) => ({
                    rect: card.getBoundingClientRect(),
                    copyWidth: text.getBoundingClientRect().width,
                }));
                // Не анимируем неизменившиеся карточки: дробные размеры могут менять отрисовку текста.
                cancelCardAnimations();
                cardAnimations = sizes.flatMap(({ card, text, width, height }, index) => {
                    const target = targets[index];
                    if (Math.abs(width - target.rect.width) < 0.5
                        && Math.abs(height - target.rect.height) < 0.5) {
                        return [];
                    }
                    // Сохраняем конечный перенос строк при изменении контура.
                    text.style.width = `${target.copyWidth}px`;
                    return [card.animate(
                        geometryFrames({ width, height }, target.rect),
                        geometryOptions
                    )];
                });
                changedText.forEach(({ text }) => {
                    cardAnimations.push(text.animate(
                        [{ opacity: 0 }, { opacity: 1 }],
                        { duration: 220, delay: 120, easing: 'ease-in-out', fill: 'both' }
                    ));
                });
                await Promise.all(cardAnimations.map(animation => animation.finished));
                if (id === transitionId) {
                    cancelCardAnimations();
                    clearSizes();
                    section.classList.remove('save-time--switching');
                }
            } catch {
                // Следующий клик отменяет текущий переход и берёт его размеры как стартовые.
                if (id === transitionId) {
                    cancelCardAnimations();
                    clearSizes();
                    applySelection();
                    section.classList.remove('save-time--switching');
                }
            }
        };

        items.forEach((item, index) => {
            item.addEventListener('click', () => selectItem(item));
            item.addEventListener('keydown', event => {
                let nextIndex;
                if (event.key === 'ArrowDown') {
                    nextIndex = (index + 1) % items.length;
                } else if (event.key === 'ArrowUp') {
                    nextIndex = (index - 1 + items.length) % items.length;
                } else if (event.key === 'Home') {
                    nextIndex = 0;
                } else if (event.key === 'End') {
                    nextIndex = items.length - 1;
                } else {
                    return;
                }
                event.preventDefault();
                items[nextIndex].focus();
            });
        });

        const finishTransition = () => {
            ++transitionId;
            section.classList.remove('save-time--switching');
            cancelCardAnimations();
            clearSizes();
            applySelection();
        };
        window.addEventListener('resize', finishTransition);
        reducedMotion.addEventListener('change', finishTransition);
        applySelection();
    });
}
